import React, { useCallback, useContext, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import _, { noop, isArray } from 'underscore';
import classNames from 'classnames';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { AssetGroup } from '@bingads-webui/campaign-odata-schemas';
import { ValidatedImageExperienceWrapper } from '@bingads-webui-campaign-react/pmax-image-experience';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import {
  CopilotRepositoryProvider,
  withBannerService,
} from '@bingads-webui/inline-copilot';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { useCopilot } from '@bingads-webui-campaign-react/copilot-experience';
import { withRecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';

import { getAdStepStyle } from '../styles/linkedIn-ad-styles';
import { CollapsibleFinalUrl } from './collapsible-final-url';
import { CollapsibleCallToAction } from './collapsible-call-to-action';
import { CollapsibleIntroductoryText } from './collapsible-introductory-text';
import { CollapsibleHeadline } from './collapsible-headline';
import { CollapsibleImages } from './collapsible-images';

import { AdPreviewConfig, LinkedInImageTypeAspectRatio, PREVIEW_IMAGE_INDEX } from '../consts';
import { MediaEditorControls } from './media-editor-controls';

const MAX_IMAGE_COUNT = 1;
const HEADLINE_MAX_LENGTH = 200;
const INTRODUCTORY_TEXT_MAX_LENGTH = 600;
const REPLACE_EXISTING_ASSETS = true;

function getLinkedInAdSchema() {
  const schema = cloneDeep(AssetGroup);
  schema.properties.Headlines.schema.properties.Headline0.maxLength = HEADLINE_MAX_LENGTH;

  schema.properties.Descriptions.schema.properties.Description0.maxLength = INTRODUCTORY_TEXT_MAX_LENGTH;

  return schema;
}

export const LinkedInAdSettings = (props) => {
  const {
    adData,
    adPreviewData,
    adPreviewState,
    onDataChange,
    isAdEditor,
  } = props;

  const classes = useDefaultStyles(getAdStepStyle);
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const perfMarker = useContext(PerformanceMarkerContext);
  const schema = useMemo(() => getLinkedInAdSchema(), []);

  const { scope } = useContext(ScopeContext);
  const instrumentation = useContext(InstrumentationContext);

  const { cid, aid } = scope.values || {};
  const { permissions } = serverContext || {};

  const {
    previewImagesSharedState,
    setPreviewImagesSharedState,
    openEditor,
    saveChanges,
  } = adPreviewState;

  const finalurls = adData && adData.FinalUrls;
  const FinalUrlForRecommendation = adData && adData.FinalUrlForRecommendation;
  const finalUrlsObj = { FinalUrls: finalurls };
  const callToAction = adData && adData.CallToAction;
  const images = adData && adData.Images;
  const selectedImageType = adData && adData.SelectedImageType;

  const maxUploadNum = useMemo(() => {
    const imageListLength = images && images.ImageList && isArray(images.ImageList) ? images.ImageList.length : 0;
    return MAX_IMAGE_COUNT - imageListLength;
  }, [images]);

  const adPreviewConfig = useMemo(() => ({
    adType: 'LinkedIn',
    placementConfig: AdPreviewConfig,
  }), []);

  const [imageExperiencePortalContainerState, setImageExperiencePortalContainerState] = useState({});
  const [imageExperienceAddNewImageCallback, setImageExperienceAddNewImageCallback] = useState(noop);
  const [addNewShortHeadlinesCallback, setAddNewShortHeadlinesCallback] = useState(noop);
  const [newCTACallback, setNewCTACallback] = useState(noop);

  const imagesContainerRef = useRef(null);

  const addNewIntroductoryTextCallBack = useCallback((newTextAssets) => {
    const newIntroductoryText = newTextAssets && newTextAssets.length > 0 ? newTextAssets[0] : '';

    onDataChange({
      data: prevData => ({
        ...prevData,
        adData: {
          ...prevData.adData,
          Descriptions: { Description0: newIntroductoryText },
        },
      }),
    });
  }, [onDataChange]);

  const onAdDataChange = useCallback((results) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        adData: {
          ...prevData.adData,
          ...results.data,
        },
      }),
      isValid: results.isValid,
      isDirty: results.isDirty,
    });
  }, [onDataChange]);

  const updateFinalUrlForRecommendation = useCallback(() => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        adData: {
          ...prevData.adData,
          FinalUrlForRecommendation: prevData.adData.FinalUrls[0],
        },
      }),
    });
  }, [onDataChange]);

  const handleReset = useCallback(() => {
    const dataPatch = {
      data: {
        Images: {
          ...adData.Images,
          ImageList: [],
          ImageAssets: null,
        },
        Headlines: {
          Headline0: '',
        },
        Descriptions: {
          Description0: '',
        },
        CallToAction: undefined,
      },
      isValid: false,
    };

    onAdDataChange(dataPatch);
  }, [adData, onAdDataChange]);

  const onUpdateFinalUrls = useCallback((newFinalUrls) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        adData: {
          ...prevData.adData,
          FinalUrls: newFinalUrls,
        },
      }),
    });
  }, [onDataChange]);

  const onRenderAllowedAspectRatioControl = useCallback(({ onAllowedAspectRatioChange }) => {
    const onSelectedAspectRatioChange = (imageType) => {
      const { cropSettings } = _.findWhere(images.ImageList[PREVIEW_IMAGE_INDEX], { type: imageType });

      onAllowedAspectRatioChange({
        cropSettings,
        imageType,
        allowedAspectRatio: LinkedInImageTypeAspectRatio[imageType],
      });
    };

    return (
      <MediaEditorControls
        adData={adData}
        imageType={selectedImageType}
        onSelectedAspectRatioChange={onSelectedAspectRatioChange}
      />
    );
  }, [adData, images, selectedImageType]);

  const handleFinalUrlKeyUpEvent = useCallback((e) => {
    const { key } = e;
    if (key === 'Enter') {
      updateFinalUrlForRecommendation();
    }
  }, [updateFinalUrlForRecommendation]);

  const finalUrlAdditionalProps = useMemo(() => ({
    placeholder: i18n.getString(_TL_('Enter your Final URL')),
    onBlur: updateFinalUrlForRecommendation,
    onKeyUp: handleFinalUrlKeyUpEvent,
  }), [i18n, updateFinalUrlForRecommendation, handleFinalUrlKeyUpEvent]);

  const customCopilotBannerStrings = {
    ApplyingSucceeded: {
      description: i18n.getString(_TL_('Select Refine to get updated recommendations by providing new descriptions or select Remove assets to remove all assets from the current ad.')),
    },
  };
  const {
    copilotBanner,
    suggestionPoller,
  } = useCopilot({
    maxUploadNum: MAX_IMAGE_COUNT,
    addNewShortHeadlinesCallback: recommendations => addNewShortHeadlinesCallback(recommendations, REPLACE_EXISTING_ASSETS),
    addNewLongHeadlinesCallback: _.noop(), // No need for LinkedIn Ad
    addNewDescriptionCallback: recommendations => addNewIntroductoryTextCallBack(recommendations, REPLACE_EXISTING_ASSETS), // Use this field as Introductory Text
    imageExperienceAddNewImageCallback: (recommendations, imageType) => imageExperienceAddNewImageCallback(recommendations, imageType, REPLACE_EXISTING_ASSETS),
    newCTACallback,
    onReset: handleReset,
    cid,
    aid,
    serverContext,
    instrumentation,
    data: adData,
    isSaving: false,
    isCanceling: false,
    enableCopilotRecommendations: true,
    feature: 'LinkedIn',
    customBannerStrings: customCopilotBannerStrings,
  });

  // Issue: after introduce this part, live-demo becomes very slow and seems have memory issue
  // Will need build up a mock data solution to fixing this issue, will have this work in E2E test
  const imageExperienceComponents = useMemo(() => (
    <div>
      <ValidatedImageExperienceWrapper
        i18n={i18n}
        permissions={permissions}
        customMaxImageCount={MAX_IMAGE_COUNT}
        imagesContaineRef={imagesContainerRef}
        updateFinalUrlCallback={onUpdateFinalUrls}
        previewImagesSharedState={previewImagesSharedState}
        setPreviewImagesSharedState={setPreviewImagesSharedState}
        updatePortalContainerState={setImageExperiencePortalContainerState}
        setImageExperienceAddNewImageCallback={setImageExperienceAddNewImageCallback}
        validationProps={{ data: adData, initialValue: adData }}
        suggestionPoller={suggestionPoller}
        wizardPerfMarker={perfMarker}
        isStockImageEnabled={false}
        isLogoEnabled={false}
        UAPLPMaxIntegration
        isCombinedMsanAndMmaAspectRatios={false}
        isLinkedInAd
        adPreviewConfig={adPreviewConfig}
        customPreviewTexts={adPreviewData}
        customOnEditImage={openEditor}
        customOnSaveEditImage={saveChanges}
        onRenderAllowedAspectRatioControl={onRenderAllowedAspectRatioControl}
      />
    </div>), [i18n, permissions, onUpdateFinalUrls, previewImagesSharedState, setPreviewImagesSharedState, adData, suggestionPoller, perfMarker, adPreviewConfig, adPreviewData, openEditor, saveChanges, onRenderAllowedAspectRatioControl]);

  return (
    <div className="linkedin-ad-section">
      <ValidationGroup
        validationProps={{
          onValidate: onAdDataChange,
          schema,
          data: adData,
          i18n,
          stopPropagation: true,
        }}
      >
        <CopilotRepositoryProvider
          campaignId="-1"
          finalUrl={FinalUrlForRecommendation}
          locale={i18n.getLocale()}
          perfMarker={perfMarker}
          recommendationType="LinkedIn"
          serverContext={serverContext}
        >
          <div className="linkedin-ad-controls-with-preview-container">
            <div className={classNames('controls-affecting-preview-container', classes.formContainer)}>
              <CollapsibleFinalUrl
                finalUrlsObj={finalUrlsObj}
                additionalProperties={finalUrlAdditionalProps}
              />
              {copilotBanner}
              <CollapsibleIntroductoryText
                schema={schema}
              />
              <CollapsibleImages
                images={images}
                imagesContainerRef={imagesContainerRef}
                imageExperiencePortalContainerState={imageExperiencePortalContainerState}
                imageExperienceAddNewImageCallback={imageExperienceAddNewImageCallback}
                maxUploadNum={maxUploadNum}
                disabled={isAdEditor}
              />
              <CollapsibleHeadline
                setAddNewShortHeadlinesCallback={setAddNewShortHeadlinesCallback}
                disabled={isAdEditor}
              />
              <CollapsibleCallToAction
                selectedOption={callToAction}
                onDataChange={onDataChange}
                setNewCTACallback={setNewCTACallback}
              />
            </div>
          </div>
          <div>
            {imageExperienceComponents}
          </div>
        </CopilotRepositoryProvider>
      </ValidationGroup>
    </div>
  );
};

LinkedInAdSettings.propTypes = {
  companyInfo: PropTypes.shape({}).isRequired,
  adData: PropTypes.shape({
    Id: PropTypes.string,
    destinationUrl: PropTypes.string,
    FinalUrls: PropTypes.arrayOf(PropTypes.string),
    FinalUrlForRecommendation: PropTypes.string,
    IntroductoryText: PropTypes.string,
    CallToAction: PropTypes.string,
    Images: PropTypes.arrayOf(PropTypes.string),
    SelectedImageType: PropTypes.string,
    isEditing: PropTypes.bool,
  }).isRequired,
  adPreviewData: PropTypes.shape({}).isRequired,
  isAdEditor: PropTypes.bool.isRequired,
  adPreviewState: PropTypes.shape({
    previewImagesSharedState: PropTypes.shape({}),
    setPreviewImagesSharedState: PropTypes.func,
    openEditor: PropTypes.func,
    saveChanges: PropTypes.func,
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
};

export const LinkedInAdSettingsShell = withRecommendationContext(withBannerService(LinkedInAdSettings));
